import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useResolvedRole, type PortalRole } from '@/hooks/useResolvedRole';

/**
 * Link to the other portal, rendered only for a user who holds BOTH roles.
 *
 * `/users/current` names one winning `role`, so staff who are also enrolled
 * land in the clinician portal — correct, and unchanged. What they had no way
 * to do was reach their own patient view: `RoleRoute` bounced them straight
 * off `/me`. It admits them now, and this is the way in that doesn't require
 * knowing to type the URL. [FOOD-1134]
 *
 * Renders nothing for everyone else, so neither shell gains a control that
 * leads somewhere the user would only be redirected away from.
 */
export const PortalSwitchLink = ({ to }: { to: PortalRole }) => {
  const { t } = useTranslation();
  const { roles } = useResolvedRole();

  if (!roles.includes('clinician') || !roles.includes('patient')) return null;

  const target = to === 'patient' ? '/me' : '/patients';
  const label = to === 'patient' ? t('My patient view') : t('Clinician portal');

  return (
    <Button variant="outline" size="sm" asChild title={label}>
      <Link to={target}>
        <ArrowLeftRight className="mr-2 h-4 w-4" />
        {label}
      </Link>
    </Button>
  );
};
